// Ported 1:1 from the stage-1 prototype scoring/matching logic. Do not "improve".

export const SECTION_ROUND = { energy: "energy", need: "need", flavor: "flavor", aesthetic: "aesthetic" };
export const ROUND_DIMENSIONS = {
  energy: ["wisdom_tradition"],
  need: ["need"],
  flavor: ["flavor"],
  aesthetic: ["art_style", "color_story"],
};
export const ROUND_ORDER = ["energy", "need", "flavor", "aesthetic"];
export const ROUND_WEIGHTS = { energy: 0.10, need: 0.20, flavor: 0.20, aesthetic: 0.50 };
export const SECTION_TITLES = {
  energy: "Round I · Energy",
  need: "Round II · Need",
  flavor: "Round III · Vibe",
  aesthetic: "Round IV · Aesthetics",
};
export const ROUND_INTRO = {
  energy: { num: "I", title: "Energy", sub: "Let's start with your energy. What kind of wisdom feels like home to you?" },
  need: { num: "II", title: "Need", sub: "Now tell me why you're really here. What are you hoping a new deck helps you with?" },
  flavor: { num: "III", title: "Vibe", sub: "Every deck has its own vibe. Let's find the ones that match yours." },
  aesthetic: { num: "IV", title: "Aesthetics", sub: "Last round, and it's my fave. Let's talk looks." },
};

export const LOADING_QUOTES = [
  "Ok, awesome! Give me a sec to work my magic...",
  "Oooooh. You two have chemistry...",
  "Okay, I did NOT expect this match for you...",
  "Hmmm... she's gorgeous, but I don't think she's got your vibe.",
  "WAIT. I think I found your deck.",
];

export const RANK_LABELS = [
  "💘 YOUR ORACLE BFF",
  "✨ YOUR BONUS BFF",
  "🔥 YOUR WILDCARD BFF",
];

export function sectionKeyOf(q) { return q.section; }

export function buildUserProfile(swipes, QUESTIONS) {
  const profile = {};
  const byId = {};
  QUESTIONS.forEach(q => (byId[q.id] = q));
  for (const qid in swipes) {
    const liked = swipes[qid];
    const q = byId[qid];
    if (!q) continue;
    q.tags.forEach(tag => {
      const delta = liked ? tag.weight : -0.5 * tag.weight;
      profile[tag.dim] = profile[tag.dim] || {};
      profile[tag.dim][tag.value] = (profile[tag.dim][tag.value] || 0) + delta;
    });
  }
  return profile;
}

function rawRoundScore(deck, profile, dims) {
  let earned = 0, possible = 0, touched = false;
  dims.forEach(dim => {
    const weights = profile[dim] || {};
    for (const value in weights) {
      const w = weights[value];
      if (w <= 0) continue;
      touched = true;
      possible += w;
      if (deck[dim] && deck[dim].includes(value)) earned += w;
    }
  });
  if (!touched || possible === 0) return null;
  return Math.max(0, Math.min(1, earned / possible));
}

export function scoreDecks(decks, swipes, QUESTIONS) {
  const profile = buildUserProfile(swipes, QUESTIONS);
  const raw = decks.map(deck => {
    const scores = {};
    ROUND_ORDER.forEach(r => (scores[r] = rawRoundScore(deck, profile, ROUND_DIMENSIONS[r])));
    return { deck, raw: scores };
  });

  const rescaled = {};
  ROUND_ORDER.forEach(r => {
    const vals = raw.map(x => x.raw[r]).filter(v => v !== null);
    if (!vals.length) { rescaled[r] = null; return; }
    const lo = Math.min(...vals), hi = Math.max(...vals);
    rescaled[r] = (v) => {
      if (v === null) return null;
      if (hi === lo) return 50;
      return Math.round((100 * (v - lo)) / (hi - lo) * 10) / 10;
    };
  });

  const results = raw.map(({ deck, raw: r }) => {
    const scores = {};
    ROUND_ORDER.forEach(rd => (scores[rd] = rescaled[rd] ? rescaled[rd](r[rd]) : null));
    let weightedSum = 0, weightTotal = 0;
    ROUND_ORDER.forEach(rd => {
      if (scores[rd] !== null) {
        weightedSum += scores[rd] * ROUND_WEIGHTS[rd];
        weightTotal += ROUND_WEIGHTS[rd];
      }
    });
    let total = weightTotal > 0 ? weightedSum / weightTotal : 0;
    if (deck.curator_pick) total += 3;
    return { deck, ...scores, total: Math.round(total * 10) / 10 };
  });
  results.sort((a, b) => b.total - a.total);
  return results;
}

export function seededRandom(seed) {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return function () { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };
}

export function pickMatches(ranked, excludeNames, poolSize, jitter, seed) {
  excludeNames = excludeNames || new Set();
  poolSize = poolSize || 20;
  jitter = jitter === undefined ? 3.0 : jitter;
  const rng = seededRandom(seed === undefined ? Date.now() % 100000 : seed);

  const pool = ranked.filter(r => !excludeNames.has(r.deck.name)).slice(0, poolSize);
  if (!pool.length) return [];

  const poolSorted = pool.slice().sort((a, b) =>
    (b.total + (rng() * 2 - 1) * jitter) - (a.total + (rng() * 2 - 1) * jitter)
  );

  const picks = [];
  const usedTiers = new Set();
  const usedPairGroups = new Set();
  let hiddenGemUsed = false;

  function eligible(r) {
    if (picks.some(p => p.deck.name === r.deck.name)) return false;
    if (r.deck.pair_group && usedPairGroups.has(r.deck.pair_group)) return false;
    if (hiddenGemUsed && r.deck.recognition === "Hidden gem") return false;
    return true;
  }
  function take(r) {
    picks.push(r);
    usedTiers.add(r.deck.recognition);
    if (r.deck.pair_group) usedPairGroups.add(r.deck.pair_group);
    if (r.deck.recognition === "Hidden gem") hiddenGemUsed = true;
  }

  const first = poolSorted.find(eligible);
  if (first) take(first);

  const second = poolSorted.find(r => eligible(r) && !usedTiers.has(r.deck.recognition));
  if (second) take(second);
  else {
    const secondFallback = poolSorted.find(eligible);
    if (secondFallback) take(secondFallback);
  }

  if (!hiddenGemUsed) {
    const gem = poolSorted.find(r => eligible(r) && r.deck.recognition === "Hidden gem");
    if (gem) take(gem);
  }
  if (picks.length < 3) {
    const next = poolSorted.find(eligible);
    if (next) take(next);
  }
  return picks.slice(0, 3);
}

export function pickExtras(matchedDecks, EXTRAS) {
  if (!EXTRAS.length) return [];
  const hasTarot = matchedDecks.some(d => d.type === "Tarot" || d.type === "Hybrid");
  const books = EXTRAS.filter(e => e.kind === "book");
  const accessories = EXTRAS.filter(e => e.kind === "accessory");

  const picks = [];
  if (hasTarot && books.length) picks.push(books[0]);

  const scored = accessories.map((a, i) => {
    let score = 0;
    const n = a.name.toLowerCase();
    if (hasTarot && n.includes("tarot")) score += 2;
    if (!n.includes("tarot")) score += 1;
    return { a, score, i };
  });
  scored.sort((x, y) => y.score - x.score || x.i - y.i);
  for (const s of scored) {
    if (picks.length >= 2) break;
    if (!picks.some(p => p.name === s.a.name)) picks.push(s.a);
  }
  return picks.slice(0, 2);
}
